import { DataGrid } from '@tableau/data-grid-3-wrapper';
import * as React from 'react';
import { ServiceLocator } from '../../app/ServiceLocator';
import { ExplorerEmptyState } from '../../app/spaces/Components/ExplorerEmpyState';
import { Formatters, Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { SortableDataGrid } from '../../common/react/components/dataGrid/SortableDataGrid3';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { imgPath } from '../../common/utilities/ImgUrl';
import { classes, cssClass } from '../../style';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';
import { SliceableFactory } from '../../ts/services/SliceableFactory';
import { sectionTitleClass } from '../styles/DefinitionSubspaceSharedStyles';
import { highlightSearchTextCellFormatter } from '../utils/highlightSearchTextCellFormatter';
import { DefinitionSubspaceValuesControls } from './DefinitionSubspaceValuesToolbar';

export interface DefinitionSubspaceValuesSectionProps {
  readonly valuesList: ReadonlyArray<string>;
  readonly valueCount: number;
}

export interface DefinitionSubspaceValuesSectionState {
  readonly filterText: string;
}

const cssPrefix = 'DefinitionSubspaceValuesSection';
const dataGridWrapperClass = cssClass(cssPrefix, 'topRow', {position: 'relative', flexGrow: 1, width: '100%', fontSize:'14px'});
const titleRealignClass = cssClass(cssPrefix, 'titleRealign', {paddingTop: '5px'});
const maxRowsMessageClass = cssClass(cssPrefix, 'titleRealign', {paddingLeft: CommonStyles.Sizes.LargeSpace, fontSize:'12px'});

export class DefinitionSubspaceValuesSection extends React.PureComponent<DefinitionSubspaceValuesSectionProps, DefinitionSubspaceValuesSectionState> {
  public constructor(props: DefinitionSubspaceValuesSectionProps){
    super(props);
    this.sliceableFactory = ServiceLocator.get<SliceableFactory>('SliceableFactory');
  }

  public state: DefinitionSubspaceValuesSectionState = {
    filterText: '',
  };

  private sliceableFactory: SliceableFactory;

  public render(): JSX.Element{
    const COUNT = Formatters.formatCount(this.props.valueCount);
    const MAXCOUNT = Formatters.formatCount(10000);
    return <React.Fragment>
      <div className={sectionTitleClass}>
        <span>
          <span className={titleRealignClass}>
            {Messages.dataRoleDefinitionSubspace_Values_Title({COUNT})}
          </span>
          {this.props.valueCount <= 10000 ? null : <span className={maxRowsMessageClass}>
            {Messages.dataRoleDefinitionSubspace_Values_MaxRowsMessage({MAXCOUNT})}
          </span>}
        </span>
        <DefinitionSubspaceValuesControls
        searchText={this.state.filterText}
        onChangeSearch={this.handleTypeInValuesSearch}/>
      </div>

      <div className={classes(dataGridWrapperClass, 'tb-datarole-values-list')}>
        {this.getDataGridContent()}
      </div>
    </React.Fragment>;
  }

  private getDataGridContent = (): JSX.Element => {
    if(this.filteredValues.length > 0) {
      return (// If there are values to show
        <SortableDataGrid
          columns={this.dataGridCols}
          getSliceable={getRowData(this.sliceableFactory, this.filteredSortedValues)}
          getRowFromData={getRowFromData}
        />);
    }

    if(this.props.valuesList.length > 0) {
      return (// If there are values, but all of them are filtered out
        <ExplorerEmptyState
          dataTestId={cssPrefix}
          iconUrl={imgPath('spaces_filter_large.svg')}
          primaryMessage={Messages.contentEmptyFilterMessage()}
          secondaryMessage={''}
          actionButtonMessage={Messages.contentEmptyClearFilters()}
          onActionButton={() => this.setState({filterText: ''})}
        />);
    }

    return (// If there are no values
      <ExplorerEmptyState
          dataTestId={cssPrefix}
          iconUrl={''}
          primaryMessage={Messages.contentEmptyMessage()}
          secondaryMessage={''}
      />);
  }

  private handleTypeInValuesSearch = (searchText: string) => this.setState({filterText: searchText});

  private get filteredValues(): ReadonlyArray<string> {
    return this.props.valuesList.filter(value => value.toLocaleLowerCase().indexOf(this.state.filterText.toLocaleLowerCase()) > -1);
  }

  private filteredSortedValues = (orderClause: ReadonlyArray<IOrderClause>):  ReadonlyArray<string> => {
    if(orderClause.length < 1 || orderClause[0].ascending) {
      return this.filteredValues;
    }
    return [...this.filteredValues].reverse();
  }

  private get dataGridCols(): DataGrid.Column[] {
    return [{
      name: 'name',
      dataKey: 'name',
      headerText: Messages.dataRoleDefinitionSubspace_Values_Name(),
      sortable: true,
      resizable: false,
      growToFit: true,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: highlightSearchTextCellFormatter(this.state.filterText),
      direction: 'asc',
    }];
  }
}


const getRowData = (sliceableFactory: SliceableFactory, getValueArray: (orderClause: ReadonlyArray<IOrderClause>) => ReadonlyArray<string>) =>
  (orderClause: ReadonlyArray<IOrderClause>) => sliceableFactory.fromArray([...getValueArray(orderClause)]);

const getRowFromData = (value: string)=> ({key: value, data: {name: value}});
